'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

/**
 * Cancel a queued cover-generation job.
 *
 * Only valid when `coverImageStatus === 'queued'` — once the worker has picked
 * up the job we cannot safely cancel without cross-process coordination with
 * the Python image-generation worker.
 *
 * This flips `coverImageStatus` to `failed` and emits `books.cover.failed` so
 * the UI exits its loading state. Token refund is NOT performed here: the
 * reservation will be settled when the worker eventually processes the
 * (now-orphaned) message — either deducting tokens on success (user keeps the
 * cover they paid for) or refunding on failure via the image-failure handler.
 */
export async function cancelCoverGeneration(input: {
  bookId: string;
}): Promise<ActionResult<{ cancelled: true }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const illustrationRepo = getIllustrationRepository();

    const illustration = await illustrationRepo.getById(input.bookId, familyId);
    if (!illustration) return { success: false, error: 'BOOK_NOT_FOUND' };

    if (illustration.coverImageStatus !== 'queued') {
      return { success: false, error: 'COVER_NOT_QUEUED' };
    }

    await illustrationRepo.update(illustration.id, familyId, { coverImageStatus: 'failed' });

    try {
      const { getEventBus } = await import('@/shared/infrastructure/events/eventBus');
      const { EVENT_TYPES } = await import('@/shared/infrastructure/events/eventTypes');
      getEventBus().emit({
        type: EVENT_TYPES.BOOKS.COVER_FAILED,
        familyId,
        payload: { bookId: illustration.id, familyId, reason: 'cancelled' },
      });
    } catch {
      /* best-effort event emit */
    }

    return { success: true, data: { cancelled: true } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('cancelCoverGeneration', e) };
  }
}
